import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import {
  UploadSimple,
  FileText,
  Trash,
  DownloadSimple,
  FolderOpen,
  WarningCircle
} from '@phosphor-icons/react';
import { format, parseISO, isValid, isBefore } from 'date-fns';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function Documents({ user }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  const [uploadForm, setUploadForm] = useState({
    file: null,
    category: 'general',
    tags: '',
    expiry_date: ''
  });

  const categories = [
    { value: 'all', label: 'All Documents' },
    { value: 'general', label: 'General' },
    { value: 'id', label: 'ID Documents' },
    { value: 'receipts', label: 'Receipts' },
    { value: 'certificates', label: 'Certificates' },
    { value: 'bills', label: 'Bills' },
    { value: 'forms', label: 'Forms' },
    { value: 'contracts', label: 'Contracts' }
  ];

  useEffect(() => { fetchDocuments(); }, [selectedCategory]);

  const fetchDocuments = async () => {
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await axios.get(`${API}/documents`, { params, withCredentials: true });
      setDocuments(response.data.items || response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => { setUploadForm({ ...uploadForm, file: e.target.files[0] }); };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      const response = await axios.post(
        `${API}/documents/upload?category=${uploadForm.category}&tags=${uploadForm.tags}&expiry_date=${uploadForm.expiry_date}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );
      setDocuments([response.data, ...documents]);
      setUploadForm({ file: null, category: 'general', tags: '', expiry_date: '' });
      setShowUploadModal(false);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${documentId}`, { withCredentials: true });
      setDocuments(documents.filter(d => d.document_id !== documentId));
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : null;
    } catch { return null; }
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    try {
      const date = parseISO(expiryDate);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      return isBefore(date, thirtyDaysFromNow);
    } catch { return false; }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      
      <main className="max-w-6xl mx-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-2">Document Vault</h1>
            <p className="text-sm md:text-base text-slate-500">Securely store and organize your documents</p>
          </div>
          <button onClick={() => setShowUploadModal(true)}
            className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm hover:shadow-md font-medium px-4 py-2 rounded-lg flex items-center gap-2"
            data-testid="upload-document-btn">
            <UploadSimple size={20} weight="bold" />
            <span className="hidden sm:inline">Upload</span>
          </button>
        </div>

        {/* Category Filter */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3 overflow-x-auto">
            <FolderOpen size={18} className="text-slate-400 flex-shrink-0" />
            <div className="flex gap-2">
              {categories.map((cat) => (
                <button key={cat.value} onClick={() => setSelectedCategory(cat.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    selectedCategory === cat.value ? 'bg-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`category-${cat.value}`}>{cat.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Documents Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-navy border-t-transparent"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="no-documents">
            <FileText size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400">No documents found. Upload your first document!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => (
              <motion.div key={doc.document_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`bg-white border rounded-lg p-4 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200 ${
                  isExpiringSoon(doc.expiry_date) ? 'border-red-200 bg-red-50' : 'border-slate-200'
                }`}
                data-testid={`document-${doc.document_id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <FileText size={24} weight="duotone" className="text-navy" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 text-sm truncate mb-1">{doc.original_filename}</h3>
                    <p className="text-xs text-slate-400">{formatFileSize(doc.size)}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex flex-wrap gap-1">
                    <span className="rounded-md px-2.5 py-0.5 text-xs font-medium border bg-blue-50 text-navy border-blue-200">{doc.category}</span>
                    {doc.tags?.map((tag, idx) => (
                      <span key={idx} className="rounded-md px-2.5 py-0.5 text-xs font-medium border bg-slate-50 text-slate-600 border-slate-200">{tag}</span>
                    ))}
                  </div>
                  {doc.expiry_date && (
                    <div className={`flex items-center gap-1 text-xs ${isExpiringSoon(doc.expiry_date) ? 'text-red-600' : 'text-slate-400'}`}>
                      {isExpiringSoon(doc.expiry_date) && <WarningCircle size={14} weight="fill" />}
                      <span>Expires: {formatDate(doc.expiry_date)}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <a href={`${API}/documents/${doc.document_id}/download`} target="_blank" rel="noopener noreferrer"
                    className="flex-1 bg-navy text-white hover:bg-navy-hover transition-all duration-200 font-medium px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                    data-testid={`download-${doc.document_id}`}>
                    <DownloadSimple size={16} weight="bold" /> View
                  </a>
                  <button onClick={() => deleteDocument(doc.document_id)}
                    className="bg-white text-slate-600 border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors font-medium px-3 py-2 rounded-lg"
                    data-testid={`delete-${doc.document_id}`}>
                    <Trash size={16} weight="regular" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-lg max-w-md w-full">
              <div className="border-b border-slate-200 p-6">
                <h2 className="text-xl font-heading font-medium tracking-tight text-slate-900">Upload Document</h2>
              </div>
              <form onSubmit={handleUpload} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">File *</label>
                  <input type="file" onChange={handleFileChange} required
                    className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                    data-testid="file-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                  <select value={uploadForm.category} onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                    data-testid="category-input">
                    {categories.filter(c => c.value !== 'all').map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tags (comma-separated)</label>
                  <input type="text" value={uploadForm.tags} onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                    placeholder="contract, compliance, 2026"
                    className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                    data-testid="tags-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Expiry Date (Optional)</label>
                  <input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                    className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2 w-full text-slate-900"
                    data-testid="expiry-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" disabled={uploading || !uploadForm.file}
                    className="flex-1 bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm font-medium px-4 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-upload-btn">{uploading ? 'Uploading...' : 'Upload'}</button>
                  <button type="button" onClick={() => { setShowUploadModal(false); setUploadForm({ file: null, category: 'general', tags: '', expiry_date: '' }); }}
                    className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-3 rounded-lg"
                    data-testid="cancel-upload-btn">Cancel</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Documents;
